import * as _ from 'lodash';

import { TromanError } from '../../../common/TromanError';
import { PaymentsCalculation, ActiveWageType } from "./PaymentsCalculation";

/**
 * @typedef { import('../../objects/Attendance').Attendance } Attendance
 */

class StandardRules {
    /**
     * @param {WageCalculation} wageCalculation
     * @param {Date} date
     */
    constructor(wageCalculation, date) {
        function findPeriod(wcwt) {
            let awt = ActiveWageType.forDate(wcwt.WageType, date);

            if (awt != null) {
                if (awt.wageType.RequireUnitPrice && awt.period.UnitPrice == null) {
                    throw new TromanError('invalid_wagetype', 'Felaktig beräkningsregel, lönearten "' + awt.wageType.display + '" saknas à-pris');
                }
            }

            return awt;
        }

        this.WageCalculation = wageCalculation;
        this.Ordinary = _.compact(_.map(wageCalculation.WageTypes.Ordinary, findPeriod));
        this.Substitute = _.compact(_.map(wageCalculation.WageTypes.Substitute, findPeriod));
        this.Serving = _.compact(_.map(wageCalculation.WageTypes.Serving, findPeriod));
        this.Travel = findPeriod(wageCalculation.WageTypes.Travel);
    }
}

export class StandardCalculationContext {
    /**
     * @param {Attendance} attendance
     */
    constructor(attendance) {
        this.attendance = attendance;
        /** @type {string} */
        this.presentTime = null;
        /** @type {string} */
        this.servingTime = null;
        this.skipMeeting = false;
        this.skipTravel = false;
    }
}

export class StandardCalculator {

    /**
     *
     * @param {Meeting} meeting
     * @param {WageCalculation} wageCalculation
     */
    constructor(meeting, wageCalculation) {
        this.accountSource = meeting.AccountSource;
        this.meetingOrganizationAccount = meeting.Organization.Account;
        this.meetingAccount = meeting.Account;
        this.rules = new StandardRules(wageCalculation, meeting.Date);
    }

    /**
     *
     * @param {StandardCalculationContext} ctx
     */
    calculateMeeting(ctx) {      
        if (ctx.attendance.isLegacy()) {
            return [];
        }

        let calculation = new StandardCalculation(ctx, this.rules);
        calculation.calculateMeeting();
        return calculation.result;
    }

    /**
     *
     * @param {StandardCalculationContext} ctx
     */
    calculateTravel(ctx) {
        if (ctx.attendance.isLegacy()) {
            return [];
        }

        let calculation = new StandardCalculation(ctx, this.rules);
        calculation.calculateTravel();
        return calculation.result;
    }
}

export class StandardCalculation extends PaymentsCalculation {

    /**
     * @param {StandardCalculationContext} ctx
     * @param {StandardRules} rules
     */
    constructor(ctx, rules) {
        super(ctx.attendance);
        this.ctx = ctx;
        this.rules = rules;
    }

    calculateMeeting() {        
        const mission = this.ctx.attendance.Mission;

        if (this.ctx.skipMeeting) return;
        if (!mission.HasMeetingPay) return;

        if (mission.MissionType.IsSubstitute) {
            this.calculateSubstitute();
        } else {
            this.calculateOrdinary();
        }
    }

    /**
     * 
     */
    calculateSubstitute() {
        let time = this.getDuration(this.ctx.presentTime);
        if (time > 0) {
            if (this.rules.Serving.length > 0) {
                const serveTime = this.getDuration(this.ctx.servingTime);
                this._calcPayments(serveTime, this.rules.Serving);
                time = time - serveTime;
            }

            this._calcPayments(time, this.rules.Substitute);
        }
    }

    /**
     * 
     */
    calculateOrdinary() {
        const time = this.getDuration(this.ctx.presentTime);
        this._calcPayments(time, this.rules.Ordinary);
    }

    /**
     * 
     * @param {number} time
     * @param {ActiveWageType[]} wts
     */
    _calcPayments(time, wts) {
        let remaining = time;

        for (let ii = 0; ii < wts.length; ++ii) {
            if (remaining <= 0) break;

            const isLast = ii === wts.length - 1;
            remaining -= this._calcMeetingPayment(wts[ii], remaining, isLast);
        }

        if (remaining > 0) {
            // TODO
        }
    }

    /**
     * @param {ActiveWageType} awt
     * @param {number} minutes
     * @param {boolean} final
     */
    _calcMeetingPayment(awt, minutes, final) {
        if (awt.wageModel.id === 0) {
            const time = final ? minutes : Math.min(minutes, awt.period.UnitPeriod);
            this.addTimePayment(awt, time);
            return time;
        } else if (awt.wageModel.id === 1) {
            const time = final ? minutes : Math.min(minutes, awt.period.UnitPeriod);
            this.addVariablePayment(awt, minutes);
            return time;
        } else if (awt.wageModel.id === 2) {
            this.addNumberPayment(awt, 1);
            return 0;
        } else {
            throw new TromanError('invalid_wage_model');
        }
    }

    /**
     */
    calculateTravel() {
        if (this.ctx.skipTravel) return;
        if (!this.rules.Travel) return;

        const km = this.attendance.Mission.TravelPayNumber;
        const time = this.getDuration(this.ctx.presentTime);
        if (km > 0 && time > 0) {
            this.addNumberPayment(this.rules.Travel, km);
        }
    }
}